import MqttGateway from './lib/mqtt_gateway';

// Renewed well inside this, so a record outliving it is the crashed-tab case.
export const VIEWER_TTL_SECONDS = 45;

/**
 * CustomerViewersGateway publishes this agent's presence on a customer, and
 * subscribes to everyone else's.
 *
 * Presence is a retained message per tab, so subscribing replays whoever is
 * currently there and there is nothing to fetch. Leaving publishes an empty
 * retained message to clear it; a tab that crashes publishes nothing and its
 * record expires.
 */
export default class CustomerViewersGateway extends MqttGateway {
  constructor(backend, requestorId) {
    super(backend, requestorId);

    // The per-tab requestor id, kept because the base class folds it into the
    // requestor topic pattern rather than holding onto it.
    this._sessionId = requestorId;
  }

  get version() {
    return '1';
  }

  get topicPattern() {
    return 'customers/+/agent-viewer/+/+';
  }

  /** Every current viewer of this customer, replayed by the broker on subscribe. */
  requestViewers(customerId) {
    return this._subscribe([customerId]);
  }

  stopRequestingViewers(customerId) {
    return this._unsubscribe([customerId]);
  }

  /** Announce or renew this agent's presence. */
  reportViewing(customerId, viewerDto) {
    return this._publishRetained(customerId, viewerDto, {
      properties: { messageExpiryInterval: VIEWER_TTL_SECONDS },
    });
  }

  /** Clear this agent's presence now rather than waiting out the expiry. */
  stopViewing(customerId) {
    // null, not '': the payload has to reach the broker as zero bytes to clear a
    // retained value, and an empty string would be published as the two bytes of
    // JSON `""` and simply replace one record with another. A zero-byte retained
    // publish deletes the record rather than storing one, so there is no expiry to
    // set on it.
    return this._publishRetained(customerId, null);
  }

  // Records arrive unwrapped, and a cleared one arrives as nothing at all — the base
  // class assumes an envelope and would dereference it.
  onReceiveSubscriber(payload, topic, packet) {
    const [customerId, agentId, sessionId] = this._broadcastTopicPattern.extractParams(topic);

    // Dropped here rather than at render, where a reader can only recognise its own
    // record while the current agent is loaded — and in the moment it isn't, our own
    // session becomes a second viewer and collapses the pill into a bare count.
    if (agentId === this._agentId) {
      return;
    }

    this.onReceive(payload, topic, { agentId, customerId, sessionId }, remainingExpiry(packet));
  }

  onReceive(payload, topic, topicParams, expiresInSeconds) {
    this.notifyObservers('onViewerChanged', payload, topicParams, expiresInSeconds);
  }

  _publishRetained(customerId, payload, options = {}) {
    // Never from a caller: the agent segment has to match the authenticated user or
    // the broker refuses the publish outright.
    const topic = this._broadcastTopicPattern.interpolate([customerId, this._agentId, this._sessionId]);

    return this._publish(topic, payload, { retain: true, ...options });
  }
}

/**
 * What the broker says is left of this record's expiry, in seconds.
 *
 * A retained record is replayed to a new subscriber with however much of its expiry
 * the broker has already spent holding it deducted (MQTT-3.3.2-6), so a peer that
 * crashed 44 seconds ago arrives with one second left rather than a fresh forty-five.
 * Absent on a live publish and on any broker that does not decrement, which is why
 * the caller falls back to the full TTL.
 *
 * Deliberately not derived from `arrivedAt`: that is minted on the publisher's
 * workstation, and the converter already discards future values because peers run
 * fast — recomputing from it would expire a slow-clocked peer early.
 */
function remainingExpiry(packet) {
  const interval = packet && packet.properties && packet.properties.messageExpiryInterval;

  return Number.isFinite(interval) && interval > 0 ? interval : undefined;
}
